import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import CustomButton from "../../components/ButtonComp";
import { useNavigate } from "react-router-dom";
import { Button } from "@chakra-ui/react";
import { baseUrl } from "../../utils/utils";

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodType, setPeriodType] = useState("Monthly");
  const [selectedMonth, setSelectedMonth] = useState("Dec 2024");

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await fetch(baseUrl + "dashboard.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: "fd=2025-07-01&td=2025-12-31",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch dashboard data");
      }

      const result = await response.json();
      if (result.success) {
        setDashboardData(result.data);
      } else {
        throw new Error("API returned unsuccessful response");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  const generateWeeklyData = () => {
    if (!dashboardData) return [];

    // Calculate average per week
    const avgOrders = Math.floor(dashboardData.total_orders / 4);
    const avgRevenue = Math.floor(dashboardData.total_revenue / 4 / 100000);

    return [
      { week: "Week 1", orders: avgOrders, revenue: avgRevenue },
      { week: "Week 2", orders: avgOrders, revenue: avgRevenue },
      { week: "Week 3", orders: avgOrders, revenue: avgRevenue },
      { week: "Week 4", orders: avgOrders, revenue: avgRevenue },
    ];
  };

  const getDeliverySlotData = () => {
    if (!dashboardData) return [];

    return [
      {
        slot: "7-10 AM",
        count: Math.floor(dashboardData["10 AM to 1 PM"] * 0.6) || 0,
        fill: "#3b82f6",
      },
      {
        slot: "10-1 PM",
        count: dashboardData["10 AM to 1 PM"] || 0,
        fill: "#10b981",
      },
      {
        slot: "1-4 PM",
        count: dashboardData["1 PM to 4 PM"] || 0,
        fill: "#fbbf24",
      },
      {
        slot: "4-7 PM",
        count: dashboardData["4 PM to 7 PM"] || 0,
        fill: "#ef4444",
      },
    ];
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(1)}Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    } else if (value >= 1000) {
      return `₹${(value / 1000).toFixed(1)}K`;
    }
    return `₹${value}`;
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#f9fafb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "64px",
              height: "64px",
              border: "4px solid #e5e7eb",
              borderTopColor: "#2563eb",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          ></div>
          <p style={{ color: "#6b7280", fontSize: "18px" }}>
            Loading dashboard data...
          </p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#f9fafb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            padding: "32px",
            borderRadius: "8px",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            maxWidth: "448px",
          }}
        >
          <div
            style={{
              fontSize: "48px",
              marginBottom: "16px",
              textAlign: "center",
            }}
          >
            ⚠️
          </div>
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#1f2937",
              marginBottom: "8px",
              textAlign: "center",
            }}
          >
            Error Loading Data
          </h2>
          <p
            style={{
              color: "#6b7280",
              textAlign: "center",
              marginBottom: "16px",
            }}
          >
            {error}
          </p>
          <button
            onClick={fetchDashboardData}
            style={{
              width: "100%",
              backgroundColor: "#2563eb",
              color: "white",
              padding: "8px 16px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const avgOrderValue = dashboardData
    ? Math.floor(dashboardData.total_revenue / dashboardData.total_orders)
    : 0;
  const activeCustomers = dashboardData
    ? dashboardData.total_orders - dashboardData.inactive_users
    : 0;
  const avgDeliveryTime = 28;
  const customerRating = 4.8;

  // Calculate growth percentages based on backend data
  const ordersGrowth = 18; // You can calculate this if you have previous month data
  const revenueGrowth = 22;
  const customersGrowth = 15;
  const deliveryGrowth = Math.floor(dashboardData?.delivery_rate || 0);

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f9fafb",
        padding: "24px",
      }}
    >
      {/* Header */}
      <div
        style={{
          marginBottom: "32px",
          display: "flex",
          alignItems: "start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "8px",
            }}
          >
            <div style={{ fontSize: "36px" }}>🏪</div>
            <h1
              style={{
                fontSize: "30px",
                fontWeight: "bold",
                color: "#1f2937",
                margin: 0,
              }}
            >
              Dashboard
            </h1>
          </div>
          <p style={{ color: "#6b7280", marginLeft: "56px", margin: 0 }}>
            Real-time order tracking, delivery performance, and customer
            analytics
          </p>
        </div>
        <div
          style={{
            display: "flex",
            gap: "16px",
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <Button
            // leftIcon={<MdAdd size={16} />}
            bg="#0175c2"
            color="white"
            size="md"
            px={4}
            onClick={() => navigate("daily-roster")}
            boxShadow="md"
            _hover={{
              bg: "#015a94",
              transform: "translateY(-1px)",
              boxShadow: "lg",
            }}
            transition="all 0.3s"
          >
            Daily Roster
          </Button>
          {/* <CustomButton
            onClick={() => navigate("daily-roster")}
            title={"Daily Roster"}
          /> */}
          <div>
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: "600",
                color: "#6b7280",
                marginBottom: "8px",
              }}
            >
              PERIOD TYPE
            </label>
            <select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
              style={{
                padding: "8px 16px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                backgroundColor: "white",
                color: "#1f2937",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              <option>Monthly</option>
              <option>Weekly</option>
              <option>Daily</option>
            </select>
          </div>
          <div>
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: "600",
                color: "#6b7280",
                marginBottom: "8px",
              }}
            >
              SELECT MONTH
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              style={{
                padding: "8px 16px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                backgroundColor: "white",
                color: "#1f2937",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              <option>Dec 2024</option>
              <option>Nov 2024</option>
              <option>Oct 2024</option>
              <option>Sep 2024</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "24px",
          marginBottom: "32px",
        }}
      >
        {/* Total Orders */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            borderLeft: "4px solid #3b82f6",
            padding: "24px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "start",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <div style={{ fontSize: "36px" }}>📦</div>
            <span
              style={{
                color: "#10b981",
                fontSize: "14px",
                fontWeight: "600",
                backgroundColor: "#d1fae5",
                padding: "4px 12px",
                borderRadius: "9999px",
              }}
            >
              ↑ {ordersGrowth}%
            </span>
          </div>
          <h3
            style={{
              color: "#6b7280",
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "8px",
              margin: 0,
            }}
          >
            TOTAL ORDERS
          </h3>
          <p
            style={{
              fontSize: "36px",
              fontWeight: "bold",
              color: "#1f2937",
              marginBottom: "4px",
              margin: 0,
            }}
          >
            {dashboardData?.total_orders.toLocaleString() || 0}
          </p>
          <p style={{ color: "#9ca3af", fontSize: "14px", margin: 0 }}>
            This month
          </p>
        </div>

        {/* Revenue */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            borderLeft: "4px solid #10b981",
            padding: "24px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "start",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <div style={{ fontSize: "36px" }}>💰</div>
            <span
              style={{
                color: "#10b981",
                fontSize: "14px",
                fontWeight: "600",
                backgroundColor: "#d1fae5",
                padding: "4px 12px",
                borderRadius: "9999px",
              }}
            >
              ↑ {revenueGrowth}%
            </span>
          </div>
          <h3
            style={{
              color: "#6b7280",
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "8px",
              margin: 0,
            }}
          >
            REVENUE
          </h3>
          <p
            style={{
              fontSize: "36px",
              fontWeight: "bold",
              color: "#1f2937",
              marginBottom: "4px",
              margin: 0,
            }}
          >
            {formatCurrency(dashboardData?.total_revenue || 0)}
          </p>
          <p style={{ color: "#9ca3af", fontSize: "14px", margin: 0 }}>
            This month
          </p>
        </div>

        {/* Active Customers */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            borderLeft: "4px solid #8b5cf6",
            padding: "24px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "start",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <div style={{ fontSize: "36px" }}>👥</div>
            <span
              style={{
                color: "#10b981",
                fontSize: "14px",
                fontWeight: "600",
                backgroundColor: "#d1fae5",
                padding: "4px 12px",
                borderRadius: "9999px",
              }}
            >
              ↑ {customersGrowth}%
            </span>
          </div>
          <h3
            style={{
              color: "#6b7280",
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "8px",
              margin: 0,
            }}
          >
            ACTIVE CUSTOMERS
          </h3>
          <p
            style={{
              fontSize: "36px",
              fontWeight: "bold",
              color: "#1f2937",
              marginBottom: "4px",
              margin: 0,
            }}
          >
            {activeCustomers.toLocaleString()}
          </p>
          <p style={{ color: "#9ca3af", fontSize: "14px", margin: 0 }}>
            This month
          </p>
        </div>

        {/* Delivery Rate */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            borderLeft: "4px solid #f59e0b",
            padding: "24px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "start",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <div style={{ fontSize: "36px" }}>🚚</div>
            <span
              style={{
                color: "#10b981",
                fontSize: "14px",
                fontWeight: "600",
                backgroundColor: "#d1fae5",
                padding: "4px 12px",
                borderRadius: "9999px",
              }}
            >
              ↑ {deliveryGrowth}%
            </span>
          </div>
          <h3
            style={{
              color: "#6b7280",
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "8px",
              margin: 0,
            }}
          >
            DELIVERY RATE
          </h3>
          <p
            style={{
              fontSize: "36px",
              fontWeight: "bold",
              color: "#1f2937",
              marginBottom: "4px",
              margin: 0,
            }}
          >
            {dashboardData?.delivery_rate.toFixed(1) || 0}%
          </p>
          <p style={{ color: "#9ca3af", fontSize: "14px", margin: 0 }}>
            On-time
          </p>
        </div>
      </div>

      {/* Charts Row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
          gap: "24px",
        }}
      >
        {/* Revenue & Orders Trend */}
        <div
          style={{
            gridColumn: "span 2",
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            padding: "24px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "24px",
            }}
          >
            <span style={{ fontSize: "24px" }}>📈</span>
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: "#1f2937",
                margin: 0,
              }}
            >
              Revenue & Orders Trend
            </h2>
          </div>
          <div style={{ display: "flex", gap: "24px", marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  width: "12px",
                  height: "12px",
                  backgroundColor: "#10b981",
                  borderRadius: "50%",
                }}
              ></div>
              <span
                style={{
                  fontSize: "14px",
                  color: "#6b7280",
                  fontWeight: "500",
                }}
              >
                Revenue (₹L)
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  width: "12px",
                  height: "12px",
                  backgroundColor: "#3b82f6",
                  borderRadius: "50%",
                }}
              ></div>
              <span
                style={{
                  fontSize: "14px",
                  color: "#6b7280",
                  fontWeight: "500",
                }}
              >
                Orders
              </span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={generateWeeklyData()}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="week"
                stroke="#9ca3af"
                style={{ fontSize: "12px" }}
              />
              <YAxis stroke="#9ca3af" style={{ fontSize: "12px" }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#10b981"
                strokeWidth={3}
                dot={{ fill: "#10b981", r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="orders"
                stroke="#3b82f6"
                strokeWidth={3}
                dot={{ fill: "#3b82f6", r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Delivery Metrics */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            padding: "24px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "24px",
            }}
          >
            <span style={{ fontSize: "24px" }}>🚚</span>
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: "#1f2937",
                margin: 0,
              }}
            >
              Delivery Metrics
            </h2>
          </div>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "32px" }}
          >
            <div
              style={{
                textAlign: "center",
                padding: "24px",
                background: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
                borderRadius: "8px",
              }}
            >
              <p
                style={{
                  fontSize: "64px",
                  fontWeight: "bold",
                  color: "#10b981",
                  marginBottom: "8px",
                  margin: 0,
                }}
              >
                {avgDeliveryTime}m
              </p>
              <p
                style={{
                  color: "#6b7280",
                  fontWeight: "600",
                  fontSize: "14px",
                  margin: 0,
                }}
              >
                AVG DELIVERY TIME
              </p>
            </div>
            <div
              style={{
                textAlign: "center",
                padding: "24px",
                background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
                borderRadius: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <p
                  style={{
                    fontSize: "64px",
                    fontWeight: "bold",
                    color: "#3b82f6",
                    margin: 0,
                  }}
                >
                  {customerRating}
                </p>
                <span style={{ fontSize: "48px" }}>⭐</span>
              </div>
              <p
                style={{
                  color: "#6b7280",
                  fontWeight: "600",
                  fontSize: "14px",
                  margin: 0,
                }}
              >
                CUSTOMER RATING
              </p>
            </div>
            <div
              style={{
                textAlign: "center",
                padding: "24px",
                background: "linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)",
                borderRadius: "8px",
              }}
            >
              <p
                style={{
                  fontSize: "64px",
                  fontWeight: "bold",
                  color: "#f59e0b",
                  marginBottom: "8px",
                  margin: 0,
                }}
              >
                ₹{avgOrderValue}
              </p>
              <p
                style={{
                  color: "#6b7280",
                  fontWeight: "600",
                  fontSize: "14px",
                  margin: 0,
                }}
              >
                AVG ORDER VALUE
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Delivery Slot Performance */}
      <div
        style={{
          marginTop: "24px",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          padding: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "24px",
          }}
        >
          <span style={{ fontSize: "24px" }}>⏰</span>
          <h2
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              color: "#1f2937",
              margin: 0,
            }}
          >
            Delivery Slot Performance
          </h2>
        </div>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={getDeliverySlotData()}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="slot"
              stroke="#9ca3af"
              style={{ fontSize: "12px" }}
            />
            <YAxis stroke="#9ca3af" style={{ fontSize: "12px" }} />
            <Tooltip
              contentStyle={{
                backgroundColor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            />
            <Bar dataKey="count" radius={[8, 8, 0, 0]}>
              {getDeliverySlotData().map((entry, index) => (
                <rect key={`bar-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default Dashboard;
